import { ApplicationDataNotice } from "./ApplicationDataNotice";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { Button } from "@/components/design/Button";
import { buttonClasses } from "@/components/design/button-classes";
import { Modal } from "@/components/design/Modal";
import { Select } from "@/components/design/Select";
import { TextInput } from "@/components/design/TextField";
import {
  ApplyToListingDocument,
  CountriesDocument,
  ListingApplicationContactDocument,
} from "@/graphql/generated/operations";
import { ingestFiles } from "@/lib/file-ingest";
import {
  MAX_APPLICATION_TOTAL_BYTES,
  MAX_DOCUMENT_UPLOAD_BYTES,
  PROJECT_DOCUMENT_ACCEPT,
} from "@/lib/upload-limits";
import { userErrorMessage } from "@/lib/user-error";
import { forgetApplicationState } from "./application-cache";
import {
  contactErrors,
  contactFieldLocked,
  contactInput,
  EMPTY_CONTACT,
  profileContact,
  type ContactField,
  type ContactValues,
} from "./application-contact";
import {
  admitApplicationDocuments,
  applicationDocumentsInput,
  NO_APPLICATION_DOCUMENTS,
  type ApplicationDocumentProblem,
  type ApplicationDocuments,
  type DocumentSlotKind,
} from "./application-documents";
import { ApplicationDocumentsSection } from "./ApplicationDocumentsSection";
import { ContactTextField } from "./ContactTextField";
import { MailLanguageField, type MailLanguageChoice } from "./MailLanguageField";
import { CommunicationLanguage } from "@/graphql/generated/operations";

// Der Bewerbungsdialog (@external-adr GENESIS-0087-application-phases-are-separate-from-decisions (GENESIS-0071 §2)).
// Eine Bewerbung trägt keinen freien Text: Kontaktdaten füllen leere Felder des eigenen Profils,
// Dokumente gehen in die Organisationsakte und sind danach für die Person selbst unsichtbar. Beides
// sagt der Dialog, bevor abgeschickt wird, nicht danach. Kein Dokument ist Pflicht, auch der Lebenslauf
// nicht (Marvin Schmidtke, 2026-10-13); genesis verlangt ebenfalls keins.
//
// Abgelehnt wird über die bestehende Fehlerbehandlung: "bereits beworben" kommt als VALIDATION ohne
// Code, eine inzwischen geschlossene Ausschreibung als "nicht gefunden". In beiden Fällen liest die
// Seite dahinter neu, damit sie zeigt, was jetzt gilt.
export function ApplyDialog({
  listingId,
  listingName,
  companyApplication = false,
  onClose,
}: {
  listingId: string;
  listingName: string;
  companyApplication?: boolean;
  onClose: () => void;
}) {
  const intl = useIntl();
  const client = useApolloClient();
  const contactQuery = useQuery(ListingApplicationContactDocument, { fetchPolicy: "no-cache" });
  const countriesQuery = useQuery(CountriesDocument);
  const [apply] = useMutation(ApplyToListingDocument);
  const [asCompany, setAsCompany] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const companyInvalid = companyApplication && (!asCompany || !companyName.trim() || companyName.trim().length > 255);
  const [values, setValues] = useState<ContactValues>(EMPTY_CONTACT);
  const [documents, setDocuments] = useState<ApplicationDocuments>(NO_APPLICATION_DOCUMENTS);
  const [language, setLanguage] = useState<MailLanguageChoice>("");
  const [problems, setProblems] = useState<ApplicationDocumentProblem[]>([]);
  const [reveal, setReveal] = useState(false);
  const [reading, setReading] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const profile = profileContact(contactQuery.data?.myself);
  const errors = contactErrors(values, profile);
  const busy = reading || pending;
  const countries = (countriesQuery.data?.countries.countries ?? [])
    .map((country) => ({ value: country.id, label: country.name ?? country.iso2 ?? country.id }))
    .sort((a, b) => a.label.localeCompare(b.label, intl.locale));

  function close() {
    if (!pending) onClose();
  }

  async function addFiles(slot: DocumentSlotKind, files: File[]) {
    if (busy || files.length === 0) return;
    setReading(true);
    try {
      const ingested = await ingestFiles(files, { maxBytes: MAX_DOCUMENT_UPLOAD_BYTES, accept: PROJECT_DOCUMENT_ACCEPT });
      // Während gelesen wird, ist jeder Platz gesperrt; `documents` ist also noch der Stand von vorher.
      const admitted = admitApplicationDocuments(documents, slot, ingested, MAX_APPLICATION_TOTAL_BYTES);
      setDocuments(admitted.next);
      setProblems(admitted.problems);
    } catch (e) {
      setProblems([]);
      setError(userErrorMessage(intl, e));
    } finally {
      setReading(false);
    }
  }

  async function submit() {
    if (busy) return;
    setReveal(true);
    setError(null);
    if (Object.keys(errors).length || companyInvalid || contactQuery.loading || contactQuery.error) return;
    setPending(true);
    try {
      const contact = contactInput(values, profile);
      const files = applicationDocumentsInput(documents).map((file) => companyApplication ? { ...file, classifier: null } : file);
      await apply({
        variables: {
          input: {
            listingId,
            ...(companyApplication && asCompany ? { companyName: companyName.trim() } : {}),
            ...(contact ? { contact } : {}),
            ...(files.length ? { documents: files } : {}),
            // Only an explicit choice; without one genesis uses the instance language, never this page's.
            ...(language ? { communicationLanguage: language === "EN" ? CommunicationLanguage.En : CommunicationLanguage.De } : {}),
          },
        },
      });
      setDone(true);
    } catch (e) {
      setError(userErrorMessage(intl, e));
    } finally {
      forgetApplicationState(client.cache, listingId);
      setPending(false);
    }
  }

  const contactField = (field: Exclude<ContactField, "countryId">, label: ReactNode) => (
    <ContactTextField
      key={field}
      idPrefix="apply-contact"
      field={field}
      label={label}
      value={contactFieldLocked(profile, field) ? profile[field] : values[field]}
      locked={contactFieldLocked(profile, field)}
      disabled={busy || contactQuery.loading}
      error={errors[field] ?? null}
      reveal={reveal}
      onChange={(next) => setValues((current) => ({ ...current, [field]: next }))}
    />
  );

  const title = (
    <FormattedMessage
      id="share.applications.dialog.title"
      defaultMessage="Bewerbung auf {name}"
      values={{ name: listingName || intl.formatMessage({ id: "share.listings.untitled", defaultMessage: "Ohne Titel" }) }}
    />
  );

  if (done) {
    return (
      <Modal
        id="apply-dialog"
        open
        onClose={onClose}
        maxWidth="40rem"
        title={title}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              <FormattedMessage id="common.close" defaultMessage="Schließen" />
            </Button>
            <Link to="/share/applications" className={buttonClasses({ variant: "primary" })}>
              <FormattedMessage id="share.applications.dialog.toMyApplications" defaultMessage="Zu meinen Bewerbungen" />{" "}
              <span aria-hidden="true">↗</span>
            </Link>
          </>
        }
      >
        <p id="apply-dialog-done" role="status" className="text-smycles-body text-ink">
          <FormattedMessage id="share.applications.dialog.done" defaultMessage="Deine Bewerbung ist eingegangen." />
        </p>
        <p className="smycles-field-meta mt-2 text-ink-muted">
          <FormattedMessage
            id="share.applications.dialog.doneHint"
            defaultMessage="Den Stand siehst du unter Meine Bewerbungen. Solange nicht entschieden ist, kannst du die Bewerbung dort zurückziehen."
          />
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      id="apply-dialog"
      open
      onClose={close}
      maxWidth="40rem"
      title={title}
      footer={
        <>
          <Button variant="secondary" disabled={pending} onClick={close}>
            <FormattedMessage id="common.cancel" defaultMessage="Abbrechen" />
          </Button>
          <Button id="apply-dialog-submit" disabled={busy} onClick={() => void submit()}>
            {pending ? (
              <FormattedMessage id="share.applications.dialog.sending" defaultMessage="Wird gesendet…" />
            ) : (
              <FormattedMessage id="share.applications.dialog.submit" defaultMessage="Bewerbung absenden" />
            )}
          </Button>
        </>
      }
    >
      <section>
        <h3 className="text-smycles-body font-medium">
          <FormattedMessage id="share.applications.contact.title" defaultMessage="Kontaktdaten" />
        </h3>
        <p className="smycles-field-meta mt-1 text-ink-muted">
          <FormattedMessage
            id="share.applications.contact.hint"
            defaultMessage="Was schon in deinem Profil steht, bleibt unverändert und ist hier fest eingetragen. Leere Felder kannst du ergänzen, sie werden in dein Profil übernommen."
          />
        </p>
        <div className="mt-3 grid grid-cols-1 gap-4 @sm:grid-cols-2">
          {contactField(
            "givenName",
            <FormattedMessage id="share.applications.contact.givenName" defaultMessage="Vorname" />,
          )}
          {contactField(
            "familyName",
            <FormattedMessage id="share.applications.contact.familyName" defaultMessage="Nachname" />,
          )}
          {contactField("phone", <FormattedMessage id="profile.page.label.phone" defaultMessage="Phone" />)}
          {contactField("website", <FormattedMessage id="profile.page.label.website" defaultMessage="Website" />)}
          {contactField("street", <FormattedMessage id="profile.page.label.street" defaultMessage="Street" />)}
          {contactField(
            "houseNumber",
            <FormattedMessage id="profile.page.label.houseNumber" defaultMessage="House number" />,
          )}
          {contactField("zipCode", <FormattedMessage id="profile.page.label.zipCode" defaultMessage="Postal code" />)}
          {contactField("city", <FormattedMessage id="profile.page.label.city" defaultMessage="Place of residence" />)}
          <label className="block">
            <span className="smycles-field-label">
              <FormattedMessage id="profile.page.label.country" defaultMessage="Country" />
            </span>
            <Select
              id="apply-contact-countryId"
              className="mt-1"
              value={contactFieldLocked(profile, "countryId") ? profile.countryId : values.countryId}
              disabled={contactFieldLocked(profile, "countryId") || busy || contactQuery.loading}
              options={
                contactFieldLocked(profile, "countryId") && !countries.some((c) => c.value === profile.countryId)
                  ? [
                      ...countries,
                      { value: profile.countryId, label: contactQuery.data?.myself?.country?.name ?? profile.countryId },
                    ]
                  : countries
              }
              onChange={(next) => setValues((current) => ({ ...current, countryId: next }))}
            />
          </label>
        </div>
      </section>

      {companyApplication ? <section className="mt-6 space-y-3">
        <label className="flex items-center gap-2 text-smycles-body">
          <input id="apply-as-company" type="checkbox" checked={asCompany} disabled={busy}
            onChange={(event) => setAsCompany(event.target.checked)} className="accent-ink focus-visible:ring-2 focus-visible:ring-ink/30" />
          <FormattedMessage id="share.applications.company.checkbox" />
        </label>
        {asCompany ? <label className="block">
          <span className="smycles-field-label"><FormattedMessage id="share.applications.company.name" /></span>
          <TextInput id="apply-company-name" className="mt-1" value={companyName} maxLength={255} disabled={busy}
            aria-invalid={reveal && companyInvalid || undefined} onChange={(event) => setCompanyName(event.target.value)} />
        </label> : null}
        {reveal && companyInvalid ? <p role="alert" className="smycles-field-meta text-ink"><FormattedMessage id="share.applications.company.required" /></p> : null}
      </section> : null}
      <ApplicationDocumentsSection
        idPrefix="apply-dialog"
        documents={documents}
        problems={problems}
        company={companyApplication}
        totalLimit={MAX_APPLICATION_TOTAL_BYTES}
        disabled={busy}
        onFiles={(slot, files) => void addFiles(slot, files)}
        onRemoveCv={() => {
          setProblems([]);
          setDocuments((current) => ({ ...current, cv: null }));
        }}
        onRemoveOther={(index) => {
          setProblems([]);
          setDocuments((current) => ({ ...current, others: current.others.filter((_, i) => i !== index) }));
        }}
      />

      <section className="mt-6">
        <MailLanguageField id="apply-mail-language" value={language} disabled={busy} onChange={setLanguage} />
        <ApplicationDataNotice company={companyApplication} />
      </section>

      {error ? (
        <p id="apply-dialog-error" role="alert" className="mt-4 text-smycles-meta text-ink-accent">
          {error}
        </p>
      ) : null}
    </Modal>
  );
}
